import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
} from 'reactstrap';
import PollTimer from './PollTimer';
import './PollDetailsModal.scss';

interface PollDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  poll: {
    id: string;
    shortCode?: string;
    title: string;
    description: string;
    condo: string;
    status: 'active' | 'closed';
    creationDate?: string;
    creationTime?: string;
    startDate?: string;
    startTime?: string;
    closingDate: string;
    closingTime: string;
    votingStart?: string;
    votingEnd?: string;
    options?: { text: string; votes?: number }[];
    mostVoted?: string;
    createdBy?: string;
  } | null;
}

const PollDetailsModal: React.FC<PollDetailsModalProps> = ({ isOpen, toggle, poll }) => {
  if (!poll) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      className="poll-details-modal"
      backdrop="static"
    >
      <ModalHeader toggle={toggle} className="poll-details-modal-header">
        <div className="poll-details-header-content">
          <div>
            <h4 className="poll-details-title">{poll.title}</h4>
            <div className="poll-details-badges">
              <Badge
                color={poll.status === 'active' ? 'success' : 'danger'}
                className="poll-details-status-badge"
              >
                {poll.status === 'active' ? 'Active' : 'Closed'}
              </Badge>
              <span className="poll-details-code">
                {poll.shortCode || `POL-${poll.id.substring(0, 8).toUpperCase()}`}
              </span>
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="poll-details-modal-body">
        {/* Poll Timer */}
        {poll.votingStart && poll.votingEnd && poll.status !== 'closed' && (
          <div className="poll-details-timer-wrapper">
            <PollTimer
              votingStart={poll.votingStart}
              votingEnd={poll.votingEnd}
              status={poll.status}
            />
          </div>
        )}

        {/* Description Section */}
        <div className="poll-details-section">
          <h6 className="poll-details-section-title">
            <i className="ri-file-text-line me-2"></i>
            Description
          </h6>
          <div className="poll-details-description">
            {poll.description || 'No description provided.'}
          </div>
        </div>

        {/* Poll Information */}
        <div className="poll-details-section">
          <h6 className="poll-details-section-title">
            <i className="ri-information-line me-2"></i>
            Poll Information
          </h6>
          <div className="poll-details-info-grid">
            <div className="poll-details-info-item">
              <span className="poll-details-info-label">
                <i className="ri-building-line me-2"></i>
                Condominium
              </span>
              <span className="poll-details-info-value">{poll.condo}</span>
            </div>
            {poll.createdBy && (
              <div className="poll-details-info-item">
                <span className="poll-details-info-label">
                  <i className="ri-user-line me-2"></i>
                  Created By
                </span>
                <span className="poll-details-info-value">{poll.createdBy}</span>
              </div>
            )}
            {poll.creationDate && poll.creationTime && (
              <div className="poll-details-info-item">
                <span className="poll-details-info-label">
                  <i className="ri-calendar-check-line me-2"></i>
                  Created On
                </span>
                <span className="poll-details-info-value">
                  {poll.creationDate} at {poll.creationTime}
                </span>
              </div>
            )}
            {poll.startDate && poll.startTime && (
              <div className="poll-details-info-item">
                <span className="poll-details-info-label">
                  <i className="ri-play-circle-line me-2"></i>
                  Starts On
                </span>
                <span className="poll-details-info-value">
                  {poll.startDate} at {poll.startTime}
                </span>
              </div>
            )}
            <div className="poll-details-info-item">
              <span className="poll-details-info-label">
                <i className="ri-time-line me-2"></i>
                {poll.status === 'active' ? 'Closes On' : 'Closed On'}
              </span>
              <span className="poll-details-info-value">
                {poll.closingDate} at {poll.closingTime}
              </span>
            </div>
          </div>
        </div>

        {/* Poll Options */}
        {poll.options && poll.options.length > 0 && (
          <div className="poll-details-section">
            <h6 className="poll-details-section-title">
              <i className="ri-list-check me-2"></i>
              Voting Options
            </h6>
            <div className="poll-details-options">
              {poll.options.map((option, idx) => (
                <div
                  key={idx}
                  className={`poll-details-option-item ${
                    poll.status === 'closed' && option.text === poll.mostVoted
                      ? 'most-voted'
                      : ''
                  }`}
                >
                  <div className="poll-details-option-content">
                    <span className="poll-details-option-number">{idx + 1}</span>
                    <span className="poll-details-option-text">{option.text}</span>
                  </div>
                  {poll.status === 'closed' && option.votes !== undefined && (
                    <Badge color="primary" className="poll-details-option-votes">
                      {option.votes} {option.votes === 1 ? 'vote' : 'votes'}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Most Voted Result */}
        {poll.status === 'closed' && poll.mostVoted && (
          <div className="poll-details-winner">
            <div className="poll-details-winner-icon">
              <i className="ri-trophy-line"></i>
            </div>
            <div className="poll-details-winner-content">
              <span className="poll-details-winner-label">Most Voted Option</span>
              <span className="poll-details-winner-value">{poll.mostVoted}</span>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="poll-details-modal-footer">
        <Button color="secondary" onClick={toggle} className="poll-details-close-btn">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PollDetailsModal;

